import { Link } from "@remix-run/react";

import type { Course } from "~/data/courses";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import common from "~/i18n/common";
import coursesDict from "~/i18n/courses";
import { useT } from "~/i18n/useT";

import "./CourseCard.scss";

/** Card in the 3-up grid: image on top, blurb beneath. */
export function CourseCard({ course }: { course: Course }) {
  const c = useT(coursesDict);
  const t = useT(common);
  const localize = useLocalizedPath();
  const copy = c[course.slug];

  return (
    <article className="ss-course-card">
      <Link
        to={localize(`/course/${course.slug}`)}
        className="ss-course-card__link"
      >
        <div className="ss-thumb ss-course-card__thumb">
          <img
            src={course.image}
            alt=""
            width={400}
            height={172}
            loading="lazy"
            decoding="async"
          />
          {course.isNew ? (
            <span className="ss-badge ss-course-card__badge">{c.badgeNew}</span>
          ) : null}
        </div>

        <div className="ss-course-card__body">
          <h3 className="ss-course-card__title">{copy.title}</h3>
          <p className="ss-course-card__short">{copy.short}</p>
          <span className="ss-link-mono">
            {t.cta.viewCourse}
            <span className="ss-flip" aria-hidden="true">
              →
            </span>
          </span>
        </div>
      </Link>
    </article>
  );
}

/** Wide row used on the courses index: thumbnail, blurb, tags, arrow. */
export function CourseRow({ course }: { course: Course }) {
  const c = useT(coursesDict);
  const localize = useLocalizedPath();
  const copy = c[course.slug];

  return (
    <article className="ss-course-row">
      <Link
        to={localize(`/course/${course.slug}`)}
        className="ss-course-row__link"
      >
        <div className="ss-thumb ss-course-row__thumb">
          <img
            src={course.image}
            alt=""
            width={200}
            height={130}
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="ss-course-row__body">
          <h3 className="ss-course-row__title">
            {copy.title}
            {course.isNew ? (
              <span className="ss-badge">{c.badgeNew}</span>
            ) : null}
          </h3>
          <p className="ss-course-row__long">{copy.long}</p>
          <div className="ss-course-row__tags">
            <span className="ss-tag ss-tag--blue">{copy.level}</span>
            <span className="ss-tag ss-tag--neutral">{copy.duration}</span>
          </div>
        </div>

        <span className="ss-course-row__arrow" aria-hidden="true">
          <span className="ss-flip">→</span>
        </span>
      </Link>
    </article>
  );
}
